import { Breadcrumbs } from "@components/atoms/Breadcrumbs"
import { Button } from "@components/atoms/Button"
import { Chip } from "@components/atoms/Chip"
import { FooterCTA } from "@components/atoms/FooterCTA"
import { Heading } from "@components/atoms/Heading"
import { CARET_RIGHT } from "@components/atoms/Icons"
import { Typography } from "@components/atoms/Typography"
import { Chips, PageContent } from "@components/atoms/single/Shared"
import {
  DocumentSidebar,
  RsrpMobileHeader,
  RsrpSidebar,
  ToolMobileHeader,
  ToolSidebar,
} from "@components/atoms/single/Sidebars"
import { Col, Container, Grid } from "@components/layout/Grid"
import { VSpace } from "@components/layout/Spacer"
import { useIsMobile } from "@hooks/useViewportDerived"
import { cn } from "@utils/tw"
import { ContentBlocks } from "eddev/blocks"
import { Link } from "eddev/routing"
import { defineView } from "eddev/views"
import { ComponentPropsWithoutRef, forwardRef, useMemo } from "react"

export default defineView("single-library-document", (props) => {
  const isMobile = useIsMobile()

  const chips = useMemo(() => {
    return [...(props?.document?.documentTopics?.nodes ?? []), ...(props?.document?.documentTypes?.nodes ?? [])]
  }, [props])

  const isRsrp: "tool" | "research" | false = useMemo(() => {
    if (props.document?.documentMeta) {
      let type: "tool" | "research" | false = false
      if (props.document.documentMeta.attachedResearch) {
        type = props.document.documentMeta.attachedResearch[0]?.__typename === "RsrpProject" ? "research" : false
      }

      if (props.document.documentMeta.attachedTool) {
        type = props.document.documentMeta.attachedTool[0]?.__typename === "RsrpProject" ? "tool" : false
      }

      return type
    }

    return false
  }, [props.document?.documentMeta])

  return (
    <>
      <Breadcrumbs uri="/research-data/library/" page={props.breadcrumbsPage} nonLinkExtra="Document" />
      <Container>
        <Grid>
          {isMobile && (
            <Col initial={12}>
              {isRsrp === "research" && (
                <>
                  {props.document?.documentMeta?.attachedResearch![0]?.__typename === "RsrpProject" && (
                    <RsrpMobileHeader
                      title={props.document.title!}
                      chips={chips}
                      projectMeta={props.document.documentMeta.attachedResearch[0].projectMeta}
                      file={props.document.relatedFile?.file ? props.document.relatedFile.file : undefined}
                      link={props.document.documentMeta.attachedResearch[0].uri!}
                      featuredImage={props.document.featuredImage?.node}
                    />
                  )}
                </>
              )}
              {isRsrp === "tool" && (
                <>
                  {props.document?.documentMeta?.attachedTool![0]?.__typename === "RsrpProject" && (
                    <ToolMobileHeader
                      title={props.document.title!}
                      chips={chips}
                      file={props.document.relatedFile?.file ? props.document.relatedFile.file : undefined}
                      link={props.document.documentMeta.attachedTool[0].uri!}
                      featuredImage={props.document.featuredImage?.node}
                    />
                  )}
                </>
              )}
              {!isRsrp && (
                <>
                  {props.document?.relatedFile?.file && (
                    <DocumentSidebar chips={chips} file={props.document.relatedFile.file} document={props.document} />
                  )}
                </>
              )}
            </Col>
          )}

          {!isMobile && (
            <Col initial={12}>
              <VSpace initial={10} />
            </Col>
          )}

          <Col initial={12} tablet={7} laptop={7}>
            <PageContent>
              {!isMobile && (
                <>
                  <Heading
                    as="h1"
                    variant="h1"
                    dangerouslySetInnerHTML={{
                      __html: `${props.document?.title} <br /><span>${props.document?.documentMeta?.subtitle ?? ""}</span>`,
                    }}
                  />

                  {chips && (
                    <>
                      <VSpace initial={3} />
                      <Chips>
                        {chips.map((chip, i) => (
                          <Chip color="outline" href={chip.uri!} key={i}>
                            {chip.name}
                          </Chip>
                        ))}
                      </Chips>
                    </>
                  )}

                  <VSpace initial={5} laptop={8} />
                </>
              )}

              <div className="blocks">
                <ContentBlocks blocks={props.document?.contentBlocks} />
              </div>

              {isRsrp === "research" && (
                <>
                  {props.document?.documentMeta?.attachedResearch![0]?.__typename === "RsrpProject" && (
                    <ProjectCardContainer
                      title={props.document.documentMeta.attachedResearch[0].title!}
                      link={props.document.documentMeta.attachedResearch[0].uri!}
                    />
                  )}
                </>
              )}
              {isRsrp === "tool" && (
                <>
                  {props.document?.documentMeta?.attachedTool![0]?.__typename === "RsrpProject" && (
                    <ProjectCardContainer
                      title={props.document.documentMeta.attachedTool[0].title!}
                      link={props.document.documentMeta.attachedTool[0].uri!}
                    />
                  )}
                </>
              )}
            </PageContent>
          </Col>

          {!isMobile && (
            <>
              <Col initial={12} tablet={1} laptop={1} />
              <Col initial={12} tablet={4} laptop={4}>
                {isRsrp === "research" && (
                  <>
                    {props.document?.documentMeta?.attachedResearch![0]?.__typename === "RsrpProject" && (
                      <RsrpSidebar
                        chips={chips}
                        projectMeta={props.document.documentMeta.attachedResearch[0].projectMeta}
                        file={props.document.relatedFile?.file ? props.document.relatedFile.file : undefined}
                        link={props.document.documentMeta.attachedResearch[0].uri!}
                        featuredImage={props.document.featuredImage?.node}
                      />
                    )}
                  </>
                )}
                {isRsrp === "tool" && (
                  <>
                    {props.document?.documentMeta?.attachedTool![0]?.__typename === "RsrpProject" && (
                      <ToolSidebar
                        chips={chips}
                        file={props.document.relatedFile?.file ? props.document.relatedFile.file : undefined}
                        link={props.document.documentMeta.attachedTool[0].uri!}
                        toolLink={props.document.documentMeta.toolLink}
                        featuredImage={props.document.featuredImage?.node}
                      />
                    )}
                  </>
                )}
                {!isRsrp && (
                  <>
                    {props.document && (
                      <DocumentSidebar
                        chips={chips}
                        file={props.document.relatedFile?.file ?? undefined}
                        document={props.document}
                      />
                    )}
                  </>
                )}
              </Col>
              <Col initial={12} tablet={1} laptop={2} />
            </>
          )}
          {props.document?.footerCTA?.ctaMessage && props.document?.footerCTA?.link?.title && (
            <Col initial={12}>
              <FooterCTA>
                <Heading
                  as="h4"
                  variant={isMobile ? "h5" : "h4"}
                  dangerouslySetInnerHTML={{ __html: props.document.footerCTA.ctaMessage }}
                />
                <Button
                  color="outline"
                  size="medium"
                  icon="ARROW_RIGHT"
                  iconStyle="right"
                  href={props.document.footerCTA.link.url ?? ""}
                  target={props.document.footerCTA.link.target ? "_blank" : undefined}
                >
                  {props.document.footerCTA.link.title}
                </Button>
              </FooterCTA>
            </Col>
          )}
        </Grid>
      </Container>
    </>
  )
})

const ProjectCardContainer = ({ title, link }: { title: string; link: string }) => {
  return (
    <ProjectCard href={link}>
      <Typography as="span" variant="caps">
        Part of project
      </Typography>
      <Typography as="h4" variant="h4">
        {title}
      </Typography>
      {CARET_RIGHT}
    </ProjectCard>
  )
}

const ProjectCard = forwardRef<HTMLAnchorElement, ComponentPropsWithoutRef<typeof Link>>(
  ({ className, ...props }, ref) => (
    <Link
      ref={ref}
      className={cn(
        "mt-8 flex flex-col border border-grey py-5 pr-8 pl-5 rounded-md gap-2 no-underline text-black relative",
        "transition-all ease-movement duration-[350ms]",
        "[&_span]:text-mid-grey",
        "[&_svg]:absolute [&_svg]:top-1/2 [&_svg]:right-4 [&_svg]:-translate-y-1/2",
        "hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]",
        className,
      )}
      {...props}
    />
  ),
)
ProjectCard.displayName = "ProjectCard"
